import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Network, Server, Lock, FileKey, AlertTriangle,
  ArrowRight, Shield,
  Database, Key, Link, Zap, ChevronDown, Clock,
  AlertCircle, Info, ExternalLink, GitBranch, Terminal
} from "lucide-react";
import { NHICorrelationGraph } from "../../../components/graph/NHICorrelationGraph";
import type { CorrelationEntry } from "../../../types/dashboard";
import { CloudEntityQuickLinks } from "./CloudEntityQuickLinks";

interface CorrelationsData {
  cloudProvider: string;
  serviceSecretMap: { service: string; secrets: string[]; nhis: number; risk: string }[];
  topNhis: { name: string; correlationCount: number; saasCorrelations: number; secretsUsed: number; services: number; riskScore: number; risk: string }[];
  topSecrets: { name: string; correlationCount: number; usedBy: number; type: string; lastRotated: string; riskScore: number; risk: string }[];
  stats: any;
  cloudSaasCorrelations?: CorrelationEntry[];
  allRelations?: CorrelationEntry[];
  insights?: any[];
}

interface ExpandedCorrelationsCardProps {
  data: CorrelationsData;
}

export const ExpandedCorrelationsCard = ({ data }: ExpandedCorrelationsCardProps) => {
  const { cloudProvider, serviceSecretMap, topNhis, topSecrets, stats, cloudSaasCorrelations, insights } = data;
  const [expandedServices, setExpandedServices] = useState<string[]>([]);
  const [expandedCorrelation, setExpandedCorrelation] = useState<number | null>(null);

  const toggleService = (service: string) => {
    setExpandedServices(prev =>
      prev.includes(service) ? prev.filter(s => s !== service) : [...prev, service]
    );
  };

  const riskIcon = (risk: string) => {
    if (risk === "high" || risk === "critical") return "text-primary";
    if (risk === "medium" || risk === "warning") return "text-sky-500";
    return "text-success";
  };

  const riskBg = (risk: string) => {
    if (risk === "high" || risk === "critical") return "bg-primary/10";
    if (risk === "medium" || risk === "warning") return "bg-sky-500/10";
    return "bg-success/10";
  };

  const severityIcon = (severity: string) => {
    const s = severity?.toLowerCase();
    if (s === "critical") return <AlertCircle className="w-4 h-4 text-primary shrink-0" />;
    if (s === "warning") return <AlertTriangle className="w-4 h-4 text-sky-500 shrink-0" />;
    return <Info className="w-4 h-4 text-sky-500 shrink-0" />;
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center gap-4">
        <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-primary/20 to-accent/10 ring-1 ring-sky-500/15 flex items-center justify-center">
          <Network className="w-5 h-5 text-sky-500" />
        </div>
        <div>
          <h3 className="font-bold text-lg text-foreground tracking-tight">NHI Correlations</h3>
          <p className="text-[15px] text-muted-foreground">Relationship mapping between services, secrets, and identities</p>
        </div>
      </div>

      {/* Stats - derived from real API details.stats */}
      <div className="grid grid-cols-4 gap-4">
        {[
          { icon: Database, label: "Unique Entities", value: stats?.uniqueEntities || "0", gradient: "from-blue-500/8 to-cyan-500/3", borderColor: "border-blue-500/15", iconBg: "bg-blue-500/15", iconColor: "text-blue-500", valueColor: "text-foreground" },
          { icon: Lock, label: "Unique Secrets", value: stats?.uniqueSecrets || "0", gradient: "from-primary/8 to-primary/3", borderColor: "border-sky-500/15", iconBg: "bg-sky-500/15", iconColor: "text-sky-500", valueColor: "text-foreground" },
          { icon: Server, label: "SaaS Platforms", value: stats?.saasPlatforms || "0", gradient: "from-emerald-500/8 to-teal-500/3", borderColor: "border-emerald-500/15", iconBg: "bg-emerald-500/15", iconColor: "text-emerald-500", valueColor: "text-foreground" },
          { icon: Link, label: "Total Relations", value: stats?.totalRelations?.toLocaleString() || "0", gradient: "from-primary/10 to-accent/5", borderColor: "border-sky-500/15", iconBg: "bg-sky-500/15", iconColor: "text-sky-500", valueColor: "text-foreground" },
        ].map((stat) => (
          <div
            key={stat.label}
            className={`relative p-5 rounded-2xl border ${stat.borderColor} bg-gradient-to-br ${stat.gradient} overflow-hidden`}
          >
            <div className={`w-9 h-9 rounded-xl flex items-center justify-center mb-3 ${stat.iconBg} ring-1 ring-black/[0.03]`}>
              <stat.icon className={`w-4 h-4 ${stat.iconColor}`} />
            </div>
            <div className={`text-3xl font-extrabold tracking-tight tabular-nums ${stat.valueColor}`}>{stat.value}</div>
            <p className="text-[12px] text-muted-foreground mt-1 font-medium">{stat.label}</p>
          </div>
        ))}
      </div>

      {/* Insights - directly from details.insights */}
      {insights && insights.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-4">
            <div className="p-1.5 rounded-lg bg-sky-500/10 ring-1 ring-sky-500/10">
              <Zap className="w-4 h-4 text-sky-500" />
            </div>
            <h3 className="font-semibold text-foreground">Critical Path Insights</h3>
            <span className="text-[12px] px-2.5 py-0.5 rounded-full bg-sky-500/10 text-sky-500 font-semibold ring-1 ring-sky-500/10">{insights.length}</span>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {insights.map((item: any) => (
              <div
                key={item.insight}
                className={`group p-4 rounded-xl border-l-[3px] border transition-all hover:-translate-y-0.5 hover:shadow-sm cursor-pointer ${
                  item.severity?.toLowerCase() === "critical" ? "border-l-destructive border-primary/15 bg-primary/[0.03]" :
                  item.severity?.toLowerCase() === "warning" ? "border-l-warning border-sky-500/15 bg-sky-500/[0.03]" :
                  "border-l-primary border-border/30 bg-card"
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className="mt-0.5">{severityIcon(item.severity)}</div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[15px] font-medium text-foreground leading-snug">{item.insight}</p>
                    <div className="flex items-center gap-2 mt-2 p-2 rounded-lg bg-muted/20">
                      <ArrowRight className="w-3 h-3 text-sky-500 shrink-0" />
                      <p className="text-[12px] text-muted-foreground">{item.recommendation}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Cloud → SaaS Correlations (from API) */}
      {cloudSaasCorrelations && cloudSaasCorrelations.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-4">
            <GitBranch className="w-4 h-4 text-sky-500" />
            <h3 className="font-semibold text-foreground">Cloud → SaaS Lateral Movements</h3>
            <span className="text-[13px] px-2 py-0.5 rounded-full bg-primary/10 text-primary font-medium">
              {cloudSaasCorrelations.length} potential pivots
            </span>
          </div>
          <div className="space-y-2">
            {cloudSaasCorrelations.map((corr) => {
               const isHigh = corr.severity === "HIGH" || corr.severity === "CRITICAL";
               return (
              <div
                key={corr.id}
                className={`rounded-xl border transition-colors ${expandedCorrelation === corr.id ? "border-sky-500/30 bg-card shadow-sm" : "border-border/40 bg-card hover:border-border"
                  }`}
              >
                <div
                  className="p-4 cursor-pointer select-none"
                  onClick={() => setExpandedCorrelation(expandedCorrelation === corr.id ? null : corr.id)}
                >
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3 overflow-hidden">
                      <div className={`w-9 h-9 shrink-0 rounded-xl flex items-center justify-center ${isHigh ? "bg-primary/10" : "bg-sky-500/10"}`}>
                        <AlertTriangle className={`w-4 h-4 ${isHigh ? "text-primary" : "text-sky-500"}`} />
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <span className="font-semibold text-[15px] text-foreground truncate">{corr.entity}</span>
                          <span className={`text-[11px] px-2 py-0.5 shrink-0 rounded-full font-bold uppercase tracking-wider ${isHigh ? "bg-primary/10 text-primary border border-primary/20" : "bg-sky-500/10 text-sky-500 border border-sky-500/20"}`}>
                            {corr.severity?.toLowerCase()}
                          </span>
                          {corr.info?.saasType && (
                            <span className="text-[11px] px-2 py-0.5 shrink-0 rounded border border-sky-500/20 bg-sky-500/10 text-sky-500 font-bold">
                              {corr.info.saasType}
                            </span>
                          )}
                          {corr.status === "ACTIVE" && (
                             <span className="text-[10px] w-2 h-2 rounded-full bg-primary animate-pulse" title="Active Threat" />
                          )}
                        </div>
                        <span className="text-[13px] text-muted-foreground flex items-center gap-1.5 flex-wrap truncate">
                          <Clock className="w-3 h-3" /> {corr.occurredAt ? new Date(corr.occurredAt).toLocaleString() : 'Recent'}
                          {corr.info?.timeDeltaSeconds !== undefined && (
                            <>
                              <span className="text-border mx-1">|</span>
                              <Zap className="w-3 h-3 text-sky-500" /> Executed in {corr.info.timeDeltaSeconds}s
                            </>
                          )}
                        </span>
                        <CloudEntityQuickLinks
                          cloudProvider={(corr.cloudType || cloudProvider || "").toLowerCase()}
                          entityKind="nhi"
                          entity={{
                            ...corr,
                            name: corr.entity,
                            serviceAccountEmail: corr.entity,
                            syncedAt: corr.occurredAt,
                          }}
                          className="mt-2"
                        />
                      </div>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <div className="flex gap-1.5 hidden md:flex">
                        {corr.entityAssociated.map((ea) => (
                          <span key={ea} className="text-[12px] px-2 py-0.5 rounded-md bg-muted/50 text-muted-foreground border border-border/40 flex items-center gap-1 max-w-[150px] truncate" title={ea}>
                            <Key className="w-2.5 h-2.5 shrink-0" /> <span className="truncate">{ea}</span>
                          </span>
                        ))}
                      </div>
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ${expandedCorrelation === corr.id ? "bg-sky-500/10" : "bg-muted hover:bg-muted/80"}`}>
                        <ChevronDown className={`w-4 h-4 ${expandedCorrelation === corr.id ? "text-primary rotate-180" : "text-muted-foreground"} transition-transform duration-200`} />
                      </div>
                    </div>
                  </div>
                </div>

                <AnimatePresence>
                  {expandedCorrelation === corr.id && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      className="overflow-hidden"
                    >
                      <div className="px-5 pb-5 pt-2 border-t border-border/20 space-y-4">
                        <div className="p-4 rounded-xl bg-primary/5 border-l-2 border-l-destructive/50 border border-border">
                          <h4 className="flex items-center gap-2 text-[13px] font-bold text-primary uppercase tracking-widest mb-1">
                            <Zap className="w-3.5 h-3.5" /> Incident Summary
                          </h4>
                          <p className="text-[15px] text-foreground/90 leading-relaxed font-medium">{corr.info?.summary || 'Detailed sequence log mapped between platforms.'}</p>
                        </div>
                        
                        {/* Interactive Graph Section */}
                        <div className="w-full h-[450px] rounded-xl bg-gradient-to-b from-card to-muted/10 border border-border overflow-hidden relative">
                          <NHICorrelationGraph
                            cloudSaasCorrelation={corr as any}
                            graphType="cloud-saas"
                          />
                          <div className="absolute top-3 right-3 text-[11px] px-2 py-1 rounded bg-background/80 backdrop-blur border text-muted-foreground flex items-center gap-1 font-medium">
                            <Network className="w-3 h-3" /> Interactive Topology
                          </div>
                        </div>

                        {/* Audit Log Terminal Blocks */}
                        <div className="grid grid-cols-2 gap-4">
                          <div className="rounded-xl border border-blue-500/30 bg-card/95 overflow-hidden flex flex-col shadow-sm">
                            <div className="bg-blue-500/10 border-b border-blue-500/20 py-2 px-3 flex items-center gap-2 shrink-0">
                              <Terminal className="w-3.5 h-3.5 text-blue-500" />
                              <span className="text-[11px] font-mono font-bold text-blue-600 uppercase tracking-wider">Cloud Audit Log</span>
                            </div>
                            <div className="p-3 font-mono text-[12px] leading-relaxed text-foreground/80 overflow-y-auto max-h-[300px] custom-scrollbar whitespace-pre-wrap break-all">
                                {corr.info?.cloudLog ? JSON.stringify(corr.info.cloudLog, null, 2) : 'No cloud logs available.'}
                            </div>
                          </div>
                          
                          <div className="rounded-xl border border-violet-500/30 bg-card/95 overflow-hidden flex flex-col shadow-sm">
                            <div className="bg-violet-500/10 border-b border-violet-500/20 py-2 px-3 flex items-center gap-2 shrink-0">
                              <Terminal className="w-3.5 h-3.5 text-violet-500" />
                              <span className="text-[11px] font-mono font-bold text-violet-600 uppercase tracking-wider">SaaS Verification Log ({corr.info?.saasType || 'Unknown'})</span>
                            </div>
                            <div className="p-3 font-mono text-[12px] leading-relaxed text-foreground/80 overflow-y-auto max-h-[300px] custom-scrollbar whitespace-pre-wrap break-all">
                                {corr.info?.saasLog ? JSON.stringify(corr.info.saasLog, null, 2) : 'No SaaS logs available.'}
                            </div>
                          </div>
                        </div>
                        
                        {corr.info?.context?.correlationReason && (
                          <div className="p-4 rounded-xl bg-sky-500/5 border border-sky-500/20 flex flex-col items-center justify-center">
                             <span className="text-[11px] font-bold text-sky-500 uppercase tracking-widest mb-1.5 flex items-center gap-1.5"><AlertTriangle className="w-3.5 h-3.5"/> Correlation Reason Match</span>
                             <span className="text-[15px] font-medium text-foreground text-center max-w-2xl">{corr.info.context.correlationReason}</span>
                          </div>
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
               );
            })}
          </div>
        </div>
      )}

      {/* Service → Secret Mapping (from props - real API data) */}
      {serviceSecretMap.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Server className="w-4 h-4 text-sky-500" />
              <h3 className="font-semibold text-foreground">Service → Secrets Mapping</h3>
              <span className="text-[13px] px-2 py-0.5 rounded-full bg-muted text-muted-foreground font-medium">{serviceSecretMap.length}</span>
            </div>
          </div>
          <div className="space-y-2">
            {serviceSecretMap.map((item) => {
              const isExpanded = expandedServices.includes(item.service);
              return (
                <div
                  key={item.service}
                  className={`rounded-xl border transition-colors ${isExpanded ? "border-sky-500/30 bg-card shadow-sm" : "border-border/40 bg-card hover:border-border"
                    }`}
                >
                  <div
                    className="p-4 cursor-pointer select-none"
                    onClick={() => toggleService(item.service)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${riskBg(item.risk)}`}>
                          <Server className={`w-4 h-4 ${riskIcon(item.risk)}`} />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-[15px] text-foreground">{item.service}</span>
                            <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium capitalize ${item.risk === "high" || item.risk === "critical"
                                ? "bg-primary/10 text-primary"
                                : item.risk === "medium"
                                  ? "bg-sky-500/10 text-sky-500"
                                  : "bg-success/10 text-success"
                              }`}>{item.risk}</span>
                          </div>
                          <span className="text-[13px] text-muted-foreground">{item.secrets.length} secrets · {item.nhis} NHIs</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="flex items-center gap-4 text-[13px] text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <Lock className="w-3 h-3" />
                            {item.secrets.length}
                          </span>
                          <span className="flex items-center gap-1">
                            <Shield className="w-3 h-3" />
                            {item.nhis}
                          </span>
                        </div>
                        <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform duration-200 ${isExpanded ? "rotate-180" : ""}`} />
                      </div>
                    </div>

                    {/* Inline secrets */}
                    <div className="mt-3 pl-12">
                      <div className="flex flex-wrap gap-1.5">
                        {item.secrets.map((secret) => (
                          <span key={secret} className="text-[12px] px-2 py-0.5 rounded-md bg-sky-500/8 text-sky-500 border border-sky-500/15 flex items-center gap-1">
                            <Key className="w-2.5 h-2.5" />
                            {secret}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Graph dropdown */}
                  <AnimatePresence>
                    {isExpanded && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="overflow-hidden"
                      >
                        <div className="px-4 pb-4">
                          <div className="w-full h-[500px] rounded-xl bg-muted/20 border border-border/30 overflow-hidden"
                            style={{ boxShadow: 'inset 0 1px 4px rgba(0,0,0,0.04)' }}
                          >
                            <NHICorrelationGraph
                              serviceData={{ service: item.service, secrets: item.secrets, risk: item.risk }}
                              graphType="service"
                            />
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>
        </div>
      )}


      {/* Bottom grid: Top NHIs + Top Secrets */}
      <div className="grid grid-cols-2 gap-6">
        {/* Top NHIs */}
        {topNhis.length > 0 && (
          <div>
            <div className="flex items-center gap-2 mb-4">
              <Database className="w-4 h-4 text-sky-500" />
              <h3 className="font-semibold text-foreground">Top NHIs by Risk</h3>
            </div>
            <div className="rounded-xl border border-border/30 overflow-hidden bg-card ring-1 ring-black/[0.02]">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border/30 bg-muted/10">
                    <th className="text-left p-3.5 text-[11px] font-semibold text-muted-foreground uppercase tracking-widest">NHI</th>
                    <th className="text-center p-3.5 text-[11px] font-semibold text-muted-foreground uppercase tracking-widest">SaaS</th>
                    <th className="text-center p-3.5 text-[11px] font-semibold text-muted-foreground uppercase tracking-widest">Links</th>
                  </tr>
                </thead>
                <tbody>
                  {topNhis.map((row, i) => (
                    <tr
                      key={row.name}
                      className={`border-t border-border/20 hover:bg-muted/20 transition-colors ${i % 2 === 0 ? "" : "bg-muted/[0.04]"}`}
                    >
                      <td className="p-3.5">
                        <div className="min-w-0">
                          <span className="font-semibold text-[15px] text-foreground">{row.name}</span>
                          <CloudEntityQuickLinks
                            cloudProvider={cloudProvider}
                            entityKind="nhi"
                            entity={{
                              ...row,
                              serviceAccountEmail: row.name,
                              name: row.name,
                            }}
                            className="mt-2"
                          />
                        </div>
                      </td>
                      <td className="p-3.5 text-center text-[15px] font-medium text-muted-foreground tabular-nums">{row.saasCorrelations}</td>
                      <td className="p-3.5 text-center text-[15px] font-medium text-muted-foreground tabular-nums">{row.correlationCount}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Top Secrets */}
        {topSecrets.length > 0 && (
          <div>
            <div className="flex items-center gap-2 mb-4">
              <FileKey className="w-4 h-4 text-sky-500" />
              <h3 className="font-semibold text-foreground">Most Connected Secrets</h3>
            </div>
            <div className="space-y-2">
              {topSecrets.map((secret, i) => (
                <div
                  key={secret.name}
                  className="group flex items-center gap-3 p-3.5 rounded-xl border border-border/20 bg-card hover:border-border/40 hover:-translate-y-0.5 hover:shadow-sm transition-all cursor-pointer"
                >
                  <div className={`w-7 h-7 rounded-lg flex items-center justify-center text-[13px] font-bold shrink-0 ring-1 ring-black/[0.03] ${i === 0 ? "bg-primary/15 text-primary" : i === 1 ? "bg-sky-500/15 text-sky-500" : "bg-sky-500/15 text-sky-500"}`}>
                    {i + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-[15px] text-foreground truncate flex items-center gap-2">
                      {secret.name}
                      <span className="text-[10px] bg-muted/50 text-muted-foreground px-1.5 py-0.5 rounded border border-border/30 tracking-wide">
                        {secret.type?.replace(/_/g, ' ')}
                      </span>
                    </div>
                    <div className="text-[12px] text-muted-foreground mt-0.5">
                      <span className="text-primary font-bold">{secret.correlationCount} Links</span> · Used by <span className="font-semibold text-foreground">{secret.usedBy}</span> NHIs · Rotated {secret.lastRotated}
                    </div>
                    <CloudEntityQuickLinks
                      cloudProvider={cloudProvider}
                      entityKind="secret"
                      entity={secret}
                      className="mt-2"
                    />
                  </div>
                  <span className={`text-[11px] px-2 py-0.5 rounded-full font-semibold capitalize shrink-0 ring-1 ${
                    secret.risk === "high" || secret.risk === "critical" ? "bg-primary/10 text-primary ring-primary/10" :
                    secret.risk === "medium" || secret.risk === "warning" ? "bg-sky-500/10 text-sky-500 ring-sky-500/10" : "bg-success/10 text-success ring-success/10"
                  }`}>{secret.risk} Risk</span>
                  <ExternalLink className="w-3.5 h-3.5 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
